/**
 * SoloHouseholdIncomeCard — v1.0.10 Item 1.
 *
 * A solo (non-group) screening IS a one-person household, so the same
 * Total Household Income field appears on the applicant profile. Rendered
 * only for screenings WITHOUT a group_id — household members see the value
 * on their household page instead. Staff edit / PM read-only; same audit
 * trail semantics as the group-level field.
 */
import { useState } from "react";
import { Wallet, Pencil } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import HouseholdIncomeEditor from "./HouseholdIncomeEditor";

export default function SoloHouseholdIncomeCard({ screening, isStaff = false, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (screening.group_id) return null;
  const thi = screening.total_household_income;
  if (thi == null && !isStaff) return null;
  const individualSum = Number((screening.decisionpoint || {}).monthly_income) || 0;

  const save = async (value) => {
    setBusy(true);
    try {
      await apiClient.patch(`/screenings/${screening.id}/household-income`,
                            { total_household_income: value });
      toast.success(value == null ? "Total Household Income cleared" : "Total Household Income saved");
      setOpen(false);
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="bg-white rounded-lg ring-1 ring-slate-200 shadow-sm overflow-hidden"
             data-testid="solo-household-income-card">
      <div className="px-5 py-3.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
            <Wallet className="h-4 w-4 text-slate-600" />
          </div>
          <div>
            <div className="ntn-eyebrow">Household of one</div>
            <div className="text-sm font-semibold text-slate-900 mt-0.5">Total Household Income</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-semibold px-2.5 py-1 rounded-full ring-1 ring-inset text-slate-700 bg-slate-50 ring-slate-200"
                data-testid="solo-household-income-value">
            {thi != null ? `$${Number(thi).toLocaleString()}/mo` : "Not set"}
          </span>
          {isStaff && (
            <button onClick={() => setOpen(true)} data-testid="edit-household-income"
                    className="text-slate-400 hover:text-slate-900 transition-colors"
                    title="Edit Total Household Income">
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      {isStaff && (
        <HouseholdIncomeEditor open={open} onOpenChange={setOpen} value={thi}
                               individualSum={individualSum} onSave={save} busy={busy} />
      )}
    </section>
  );
}
